// Dashboard layout engine helpers shared by the Builder canvas, the Combine
// container, the Properties panel and the widget library drag source.
// One authoritative model: widget.layout {x,y,w,h} in grid units (12-col default),
// children of a container use widget.layout_child in the container's 12-col grid.

export const WIDGET_MIME = "application/x-eip-widget";
export const DROPPING_ID = "__dropping__";
export const GRID = { ROW_H: 56, MARGIN: 16, PAD: 16 };

export const DEFAULT_SIZE = {
  kpi: { w: 3, h: 3 }, gauge: { w: 3, h: 4 }, liquidfill: { w: 3, h: 4 }, slicer: { w: 3, h: 3 }, text: { w: 4, h: 3 },
  combo_box: { w: 3, h: 3 }, list_box: { w: 3, h: 6 }, date_picker: { w: 4, h: 3 }, container: { w: 8, h: 8 },
  table: { w: 6, h: 5 }, bar_3d: { w: 6, h: 6 }, scatter_3d: { w: 6, h: 6 }, sankey: { w: 7, h: 6 }, graph: { w: 6, h: 6 },
  sunburst: { w: 5, h: 6 }, wordcloud: { w: 5, h: 5 }, candlestick: { w: 7, h: 5 }, calendar_heatmap: { w: 9, h: 4 },
  boxplot: { w: 6, h: 5 }, waterfall: { w: 6, h: 5 }, _default: { w: 6, h: 5 },
};

// Smallest usable size per widget type (grid units). Never 0 / negative.
export const MIN_SIZE = {
  kpi: { w: 2, h: 2 }, text: { w: 2, h: 1 }, image: { w: 1, h: 1 }, richtext: { w: 2, h: 1 },
  slicer: { w: 2, h: 2 }, combo_box: { w: 2, h: 1 }, list_box: { w: 2, h: 2 }, date_picker: { w: 2, h: 1 },
  container: { w: 3, h: 3 }, table: { w: 3, h: 3 }, pivot: { w: 3, h: 3 }, data_grid: { w: 3, h: 3 },
  _default: { w: 2, h: 2 },
};

export function defaultSizeFor(type, cols = 12) {
  const s = DEFAULT_SIZE[type] || DEFAULT_SIZE._default;
  const scale = cols / 12;
  return { w: Math.max(1, Math.min(cols, Math.round(s.w * scale))), h: s.h };
}

export function minSizeFor(type) {
  return MIN_SIZE[type] || MIN_SIZE._default;
}

const int = (v, d = 0) => { const n = Math.round(Number(v)); return Number.isFinite(n) ? n : d; };

// Clamp a layout to valid integers inside the grid: min size, max width, x within bounds, y >= 0.
export function clampLayout(l, type, cols = 12) {
  const min = minSizeFor(type);
  const w = Math.max(min.w, Math.min(cols, int(l?.w, min.w)));
  const h = Math.max(min.h, int(l?.h, min.h));
  const x = Math.max(0, Math.min(cols - w, int(l?.x, 0)));
  const y = Math.max(0, int(l?.y, 0));
  return { ...(l || {}), x, y, w, h };
}

// Pixel offset inside the grid element -> grid coordinates (top-left of an item of size w×h).
export function pxToGrid(px, py, { cols = 12, width, rowH = GRID.ROW_H, margin = GRID.MARGIN, pad = GRID.PAD, w = 1, h = 1 } = {}) {
  const colW = (width - margin * (cols - 1) - pad * 2) / cols;
  const x = Math.round((px - pad) / (colW + margin));
  const y = Math.round((py - pad) / (rowH + margin));
  return { x: Math.max(0, Math.min(cols - w, x)), y: Math.max(0, y) };
}

// Same conversion but for a point inside a Combine container body (12 cols, cfg.row_h + gap).
export function containerPointToGrid(cx, cy, rect, cc, w, h, scale = 1) {
  const gap = Number(cc?.gap ?? 8);
  const rowH = Number(cc?.row_h || 40) + gap;
  const cw = Math.min(w, 12);
  const gx = Math.max(0, Math.min(12 - cw, Math.floor(((cx - rect.left) / Math.max(1, rect.width)) * 12)));
  const gy = Math.max(0, Math.round(((cy - rect.top) / Math.max(0.01, scale || 1)) / Math.max(1, rowH)));
  return { x: gx, y: gy, w: cw, h };
}

export function collides(a, b) {
  if (!a || !b || a.i === b.i) return false;
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

// Nearest free slot for `item` among `others` (all {x,y,w,h}): search rings around the
// requested position, preferring same column then increasing distance. Used in free
// (non-compacting) mode and for paste so widgets land close to where the user pointed.
export function findFreeSpot(others, item, cols = 12, maxRadius = 60) {
  const fits = (x, y) => x >= 0 && x + item.w <= cols && y >= 0 && !others.some((o) => collides({ ...item, x, y }, o));
  if (fits(item.x, item.y)) return { x: item.x, y: item.y };
  for (let r = 1; r <= maxRadius; r++) {
    const cands = [];
    for (let dx = -r; dx <= r; dx++) { cands.push([item.x + dx, item.y + r]); cands.push([item.x + dx, item.y - r]); }
    for (let dy = -r + 1; dy <= r - 1; dy++) { cands.push([item.x + r, item.y + dy]); cands.push([item.x - r, item.y + dy]); }
    cands.sort((a, b) => (Math.abs(a[0] - item.x) + Math.abs(a[1] - item.y)) - (Math.abs(b[0] - item.x) + Math.abs(b[1] - item.y)) || (a[1] - b[1]));
    for (const [x, y] of cands) if (fits(x, y)) return { x, y };
  }
  const maxY = others.reduce((m, o) => Math.max(m, o.y + o.h), 0);
  return { x: Math.max(0, Math.min(cols - item.w, item.x)), y: maxY };
}

// Translate a set of widgets (keeping their relative arrangement) so the group's
// top-left lands on `anchor`; clamp into the grid. Returns new layouts by widget id.
export function placeGroupAt(items, anchor, cols = 12) {
  if (!items.length) return {};
  const minX = Math.min(...items.map((w) => w.layout?.x || 0));
  const minY = Math.min(...items.map((w) => w.layout?.y || 0));
  const out = {};
  items.forEach((w) => {
    const l = w.layout || {};
    const width = Math.min(l.w || 6, cols);
    out[w.id] = {
      ...l, w: width, h: l.h || 4,
      x: Math.max(0, Math.min(cols - width, (l.x || 0) - minX + anchor.x)),
      y: Math.max(0, (l.y || 0) - minY + anchor.y),
    };
  });
  return out;
}

// Resolve overlaps for `placed` (map id -> layout) against `existing` layouts, in
// free mode (no compaction). Items are processed top-left first; earlier placed items
// count as obstacles for later ones so a pasted group never lands on itself.
export function resolveFree(placed, existing, cols = 12) {
  const obstacles = existing.map((l) => ({ ...l }));
  const order = Object.keys(placed).sort((a, b) => (placed[a].y - placed[b].y) || (placed[a].x - placed[b].x));
  const out = {};
  order.forEach((id) => {
    const l = placed[id];
    const spot = findFreeSpot(obstacles, { ...l, i: id }, cols);
    out[id] = { ...l, ...spot };
    obstacles.push({ ...out[id], i: id });
  });
  return out;
}

// ---- Widget library drag payload (HTML5 dataTransfer) ----------------------
export function writeWidgetDrag(e, payload) {
  try {
    e.dataTransfer.setData(WIDGET_MIME, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "copy";
  } catch { /* ignore */ }
}

export function readWidgetDrag(e) {
  try {
    const raw = e.dataTransfer?.getData(WIDGET_MIME);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function isWidgetDrag(e) {
  const types = e?.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes(WIDGET_MIME);
}

// Container body element under the pointer (excluding `exclude` subtree). Returns the id or null.
export function containerUnderPoint(cx, cy, exclude = null) {
  if (cx == null || cy == null || typeof document === "undefined") return null;
  const stack = document.elementsFromPoint(cx, cy);
  const el = stack.find((n) => n.hasAttribute && n.hasAttribute("data-container-id") && (!exclude || !exclude.contains(n)));
  return el ? { id: el.getAttribute("data-container-id"), el } : null;
}
